import React, { useState, useMemo, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown, Search, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

export default React.memo(function ElementCombobox({ elements, value, onValueChange, onRequestNew, placeholder = "Select element..." }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  const selectedElement = elements.find(e => e.id === value);

  const filtered = useMemo(() => {
    if (!search) return elements;
    const lower = search.toLowerCase();
    return elements.filter(e =>
      e.canonical_name?.toLowerCase().includes(lower) ||
      e.data_type?.toLowerCase().includes(lower)
    );
  }, [elements, search]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  return (
    <div className="flex w-full items-center gap-0 rounded-md border border-input">
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            className="flex flex-1 min-w-0 items-center justify-between px-3 h-9 text-sm bg-transparent outline-none"
          >
            <span className={cn("truncate", !selectedElement && "text-muted-foreground")}>
              {selectedElement
                ? `${selectedElement.canonical_name} (${selectedElement.data_type})`
                : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              placeholder="Search elements..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">No elements found.</div>
            ) : (
              filtered.map(element => (
                <div
                  key={element.id}
                  onClick={() => {
                    onValueChange(element.id);
                    setOpen(false);
                  }}
                  className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === element.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="truncate">
                    {element.canonical_name} ({element.data_type})
                  </span>
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
      {onRequestNew && (
        <button
          type="button"
          onClick={onRequestNew}
          title="Create new element"
          className="flex items-center justify-center h-9 w-9 shrink-0 border-l border-input text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <Plus className="h-4 w-4" />
        </button>
      )}
    </div>
  );
});
